import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { reviewApi } from '../../services/api';
import { PageHeader, Spinner, EmptyState, Modal, ConfirmModal } from '../../components/shared/index';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  PlusIcon, ArrowUpIcon, ArrowDownIcon, TrashIcon,
  PencilIcon, ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';

const TYPES   = ['manager','peer','self','client','skip_level','upward'];
const Q_TYPES = [
  { id: 'rating',       label: 'Rating (1–N)' },
  { id: 'text',         label: 'Free text' },
  { id: 'yes_no',       label: 'Yes / No' },
  { id: 'multi_select', label: 'Multi-select' },
];
const ROLES = ['manager','project_manager','developer','designer','qa','sales','cs','hr','operations','other'];
const BANDS = ['junior','mid','senior','staff','principal','manager','director','vp','c_level'];

function genId() { return 'q_' + Math.random().toString(36).slice(2, 9); }

function QuestionEditor({ q, onChange, onRemove, onUp, onDown, isFirst, isLast }) {
  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
      <div className="flex items-start gap-2">
        <div className="flex-1 space-y-3">
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="label">Label *</label>
              <input className="input" value={q.label} onChange={e => onChange({ ...q, label: e.target.value })} placeholder="e.g. Quality of technical work" />
            </div>
            <div className="w-44">
              <label className="label">Type</label>
              <select className="input" value={q.type} onChange={e => onChange({ ...q, type: e.target.value })}>
                {Q_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>
          </div>

          {q.type === 'rating' && (
            <div className="w-32">
              <label className="label">Scale</label>
              <input type="number" min={2} max={10} className="input" value={q.scale || 5}
                onChange={e => onChange({ ...q, scale: Number(e.target.value) })} />
            </div>
          )}

          {q.type === 'multi_select' && (
            <div>
              <label className="label">Options (one per line)</label>
              <textarea className="input h-20 resize-none" value={(q.options || []).join('\n')}
                onChange={e => onChange({ ...q, options: e.target.value.split('\n').map(s => s.trim()).filter(Boolean) })}
                placeholder={'Strongly agree\nAgree\nNeutral\nDisagree\nStrongly disagree'} />
            </div>
          )}

          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={!!q.required} onChange={e => onChange({ ...q, required: e.target.checked })} />
            Required
          </label>
        </div>

        <div className="flex flex-col gap-1">
          <button type="button" disabled={isFirst} onClick={onUp} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30"><ArrowUpIcon className="w-4 h-4" /></button>
          <button type="button" disabled={isLast}  onClick={onDown} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30"><ArrowDownIcon className="w-4 h-4" /></button>
          <button type="button" onClick={onRemove} className="p-1 text-red-500 hover:text-red-700"><TrashIcon className="w-4 h-4" /></button>
        </div>
      </div>
    </div>
  );
}

function TemplateForm({ initial, onSave, onClose }) {
  const [form, setForm] = useState(() => initial ?? {
    name: '', type: 'manager', is_anonymous: false,
    applicable_roles: [], applicable_bands: [],
    questions: [{ id: genId(), type: 'rating', label: '', required: true, scale: 5 }],
  });

  const setQ = (i, q) => setForm(f => {
    const qs = [...f.questions]; qs[i] = q; return { ...f, questions: qs };
  });
  const addQ = () => setForm(f => ({ ...f, questions: [...f.questions, { id: genId(), type: 'rating', label: '', required: true, scale: 5 }] }));
  const rmQ  = i => setForm(f => ({ ...f, questions: f.questions.filter((_, j) => j !== i) }));
  const moveQ = (i, dir) => setForm(f => {
    const qs = [...f.questions];
    const j = i + dir;
    if (j < 0 || j >= qs.length) return f;
    [qs[i], qs[j]] = [qs[j], qs[i]];
    return { ...f, questions: qs };
  });
  const toggle = (field, val) => setForm(f => {
    const list = new Set(f[field] || []);
    list.has(val) ? list.delete(val) : list.add(val);
    return { ...f, [field]: Array.from(list) };
  });

  const valid = form.name.trim() && form.questions.length > 0 && form.questions.every(q => q.label.trim());

  return (
    <form onSubmit={e => { e.preventDefault(); onSave(form); }} className="space-y-5">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label">Template name *</label>
          <input required className="input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
        </div>
        <div>
          <label className="label">Review type *</label>
          <select className="input" value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
            {TYPES.map(t => <option key={t} value={t}>{t.replace('_',' ')}</option>)}
          </select>
        </div>
      </div>

      <label className="inline-flex items-center gap-2 text-sm text-gray-700">
        <input type="checkbox" checked={!!form.is_anonymous} onChange={e => setForm(f => ({ ...f, is_anonymous: e.target.checked }))} />
        Collect anonymously (identity hidden from reviewee)
      </label>

      <div>
        <label className="label">Applicable to roles (empty = all)</label>
        <div className="flex flex-wrap gap-1">
          {ROLES.map(r => (
            <button key={r} type="button" onClick={() => toggle('applicable_roles', r)}
              className={clsx('px-2 py-0.5 rounded-full text-xs border capitalize',
                (form.applicable_roles || []).includes(r) ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-white border-gray-200 text-gray-600')}>
              {r.replace('_',' ')}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="label">Applicable to bands (empty = all)</label>
        <div className="flex flex-wrap gap-1">
          {BANDS.map(b => (
            <button key={b} type="button" onClick={() => toggle('applicable_bands', b)}
              className={clsx('px-2 py-0.5 rounded-full text-xs border',
                (form.applicable_bands || []).includes(b) ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-white border-gray-200 text-gray-600')}>
              {b}
            </button>
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="label mb-0">Questions</label>
          <button type="button" onClick={addQ} className="btn-secondary btn-sm"><PlusIcon className="w-3.5 h-3.5" /> Add question</button>
        </div>
        <div className="space-y-3">
          {form.questions.map((q, i) => (
            <QuestionEditor key={q.id} q={q}
              onChange={nq => setQ(i, nq)}
              onRemove={() => rmQ(i)}
              onUp={() => moveQ(i, -1)}
              onDown={() => moveQ(i, 1)}
              isFirst={i === 0}
              isLast={i === form.questions.length - 1}
            />
          ))}
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
        <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
        <button type="submit" disabled={!valid} className="btn-primary disabled:opacity-40">
          {initial ? 'Save changes' : 'Create template'}
        </button>
      </div>
    </form>
  );
}

export default function ReviewTemplates() {
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [deleting, setDeleting] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['review-templates'],
    queryFn:  () => reviewApi.templates.list().then(r => r.data),
  });

  const createMut = useMutation({
    mutationFn: reviewApi.templates.create,
    onSuccess:  () => { qc.invalidateQueries(['review-templates']); setCreating(false); toast.success('Template created'); },
    onError:    e  => toast.error(e.response?.data?.message || 'Failed to create'),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, data }) => reviewApi.templates.update(id, data),
    onSuccess:  () => { qc.invalidateQueries(['review-templates']); setEditing(null); toast.success('Template updated'); },
    onError:    e  => toast.error(e.response?.data?.message || 'Failed to update'),
  });

  const deleteMut = useMutation({
    mutationFn: id => reviewApi.templates.delete(id),
    onSuccess:  () => { qc.invalidateQueries(['review-templates']); setDeleting(null); toast.success('Archived'); },
    onError:    e  => toast.error(e.response?.data?.message || 'Failed to delete'),
  });

  const templates = Array.isArray(data) ? data : (data?.data || []);

  if (isLoading) return <div className="flex justify-center p-20"><Spinner size="lg" /></div>;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <PageHeader
        title="Review Templates"
        subtitle="Question sets that drive self, peer, manager, client, and 360 reviews"
        actions={<button onClick={() => setCreating(true)} className="btn-primary"><PlusIcon className="w-4 h-4" /> New template</button>}
      />

      {templates.length === 0 ? (
        <EmptyState title="No templates yet"
          description="Create a template to standardise question sets across cycles."
          action={<button onClick={() => setCreating(true)} className="btn-primary">Create template</button>} />
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {templates.map(t => (
            <div key={t.id} className="card p-5 flex flex-col">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 bg-blue-50 text-blue-600 rounded-lg flex items-center justify-center">
                    <ClipboardDocumentListIcon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{t.name}</p>
                    <p className="text-xs text-gray-500 capitalize">{t.type?.replace('_',' ')}{t.is_anonymous ? ' · anonymous' : ''}</p>
                  </div>
                </div>
                {t.is_active === false && <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">Archived</span>}
              </div>

              <div className="text-sm text-gray-600 mb-3">
                <span className="font-medium text-gray-800">{(t.questions || []).length}</span> question{(t.questions || []).length === 1 ? '' : 's'}
              </div>

              {(t.applicable_roles?.length || t.applicable_bands?.length) ? (
                <div className="flex flex-wrap gap-1 mb-3">
                  {(t.applicable_roles || []).map(r => (
                    <span key={`r-${r}`} className="px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-700 capitalize">{r.replace('_',' ')}</span>
                  ))}
                  {(t.applicable_bands || []).map(b => (
                    <span key={`b-${b}`} className="px-2 py-0.5 rounded-full text-xs bg-purple-50 text-purple-700">{b}</span>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-400 mb-3">Applies to all roles / bands</p>
              )}

              <div className="mt-auto flex gap-2 pt-3 border-t border-gray-100">
                <button onClick={() => setEditing(t)} className="btn-secondary btn-sm"><PencilIcon className="w-3.5 h-3.5" /> Edit</button>
                <button onClick={() => setDeleting(t)} className="btn-secondary btn-sm text-red-600"><TrashIcon className="w-3.5 h-3.5" /> Archive</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={creating} onClose={() => setCreating(false)} title="New review template" size="xl">
        <TemplateForm
          onSave={d => createMut.mutate(d)}
          onClose={() => setCreating(false)}
        />
      </Modal>

      <Modal open={!!editing} onClose={() => setEditing(null)} title={`Edit template — ${editing?.name}`} size="xl">
        {editing && (
          <TemplateForm
            initial={editing}
            onSave={d => updateMut.mutate({ id: editing.id, data: d })}
            onClose={() => setEditing(null)}
          />
        )}
      </Modal>

      <ConfirmModal
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleteMut.mutate(deleting.id)}
        title="Archive template?"
        message={`"${deleting?.name}" will no longer appear in the new-review dialog. You can restore it later.`}
        danger
      />
    </div>
  );
}
